/**
 * API client for Hestia backend
 * Handles authentication, token refresh, and API requests
 */

import { getApiBaseUrl } from '../config/constants';
import { getStorageItem, setStorageItem, removeStorageItem } from '../utils/storage';
import { TOKEN_EXPIRATION_BUFFER } from '../utils/constants';
import { error as logError } from '../utils/logger';
import { isApiError, type ApiError, type ApiResult, type FetchOptions } from '../types';

interface CognitoTokenResponse {
  access_token: string;
  id_token: string;
  expires_in: number;
}

/**
 * Refreshes the access token using the refresh token
 */
export const refreshToken = async (): Promise<ApiResult<CognitoTokenResponse>> => {
  try {
    const refreshToken = getStorageItem('refreshToken') as string | null;
    if (!refreshToken) {
      throw new Error('No refresh token found');
    }

    const region = import.meta.env.VITE_AWS_REGION || 'us-east-1';
    const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID;
    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;

    if (!userPoolId || !clientId) {
      // Return error instead of throwing - let caller handle it gracefully
      return {
        data: null,
        error: new Error('Cognito configuration missing. Please set VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID environment variables.'),
      };
    }

    const response = await fetch(`https://cognito-idp.${region}.amazonaws.com/${userPoolId}/oauth2/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: clientId,
        refresh_token: refreshToken,
      }).toString(),
      mode: 'cors',
    });

    if (!response.ok) {
      throw new Error('Failed to refresh token');
    }

    const data: CognitoTokenResponse = await response.json();

    // Save new tokens
    setStorageItem('accessToken', data.access_token);
    setStorageItem('idToken', data.id_token);
    setStorageItem('expiresIn', Date.now() + (data.expires_in * 1000).toString());

    return { data, error: null };
  } catch (error) {
    // Log error (guarded by logger utility)
    logError('Error refreshing token:', error);
    return { data: null, error: error instanceof Error ? error : new Error(String(error)) };
  }
};

/**
 * Helper function for authenticated API requests.
 * @typeParam T - Expected shape of the parsed JSON response. Callers (the `api/*`
 *   modules) should pass a concrete model, e.g. `fetchWithAuth<Paginated<Lead>>(...)`.
 *   MIGRATION NOTE: the default is `any` (not `unknown`) so that any not-yet-typed
 *   call site keeps compiling under strict mode rather than forcing `unknown`
 *   narrowing everywhere at once. Tighten to `unknown` once all callers pass `T`.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchWithAuth = async <T = any>(
  endpoint: string,
  options: FetchOptions = {},
): Promise<T> => {
  const token = getStorageItem('accessToken') as string | null;
  const expiresIn = getStorageItem('expiresIn') as string | null;
  const url = `${getApiBaseUrl()}${endpoint}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
  };

  // Check if token is expired or about to expire (within buffer time)
  // Only attempt refresh if we have Cognito configuration
  const hasCognitoConfig = import.meta.env.VITE_COGNITO_USER_POOL_ID && import.meta.env.VITE_COGNITO_CLIENT_ID;

  if (token && expiresIn && Date.now() > parseInt(expiresIn) - TOKEN_EXPIRATION_BUFFER && hasCognitoConfig) {
    const { data, error } = await refreshToken();
    if (error || !data) {
      // If refresh fails, clear tokens and throw error
      removeStorageItem('accessToken');
      removeStorageItem('refreshToken');
      removeStorageItem('expiresIn');
      throw { status: 401, message: 'Session expired. Please sign in again.' } satisfies ApiError;
    }
    headers['Authorization'] = `Bearer ${data.access_token}`;
  } else if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      mode: 'cors',
    });

    if (!response.ok) {
      let errorMessage = 'An error occurred';
      try {
        const errorText = await response.text();
        if (errorText) {
          try {
            const errorData = JSON.parse(errorText);
            // FastAPI wraps error details in a 'detail' field
            // If detail is an object with 'message', extract it; otherwise use detail as string or fallback to message
            if (errorData.detail) {
              errorMessage = typeof errorData.detail === 'string'
                ? errorData.detail
                : errorData.detail.message || errorData.detail;
            } else {
              errorMessage = errorData.message || errorMessage;
            }
          } catch {
            // If JSON parse fails, use the raw text if available
            errorMessage = errorText || errorMessage;
          }
        }
      } catch {
        errorMessage = response.statusText || errorMessage;
      }

      // Check for account status errors (waitlist)
      if (response.status === 403 && errorMessage?.includes('not active')) {
        throw {
          status: response.status,
          message: errorMessage,
          isWaitlist: true,
        } satisfies ApiError;
      }

      throw {
        status: response.status,
        message: errorMessage,
      } satisfies ApiError;
    }

    // Handle empty responses
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const data = await response.json();
      return data as T;
    }

    return null as T;
  } catch (error) {
    // If error already has a status (HTTP error), re-throw it
    if (isApiError(error)) {
      throw error;
    }

    // Network errors (fetch failures, CORS, etc.) don't have a status
    // Mark them explicitly so they can be distinguished from auth errors
    throw {
      status: 0, // Use 0 for network errors (browser convention)
      message: error instanceof Error ? error.message : 'Network error. Please check your connection and try again.',
      isNetworkError: true,
      originalError: error,
    } satisfies ApiError;
  }
};

/**
 * Helper function for authenticated API requests that return binary data.
 */
export const fetchBlobWithAuth = async (
  endpoint: string,
  options: FetchOptions = {},
): Promise<{ blob: Blob; headers: Headers }> => {
  const token = getStorageItem('accessToken') as string | null;
  const expiresIn = getStorageItem('expiresIn') as string | null;
  const url = `${getApiBaseUrl()}${endpoint}`;

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> | undefined),
  };

  const hasCognitoConfig = import.meta.env.VITE_COGNITO_USER_POOL_ID && import.meta.env.VITE_COGNITO_CLIENT_ID;
  if (token && expiresIn && Date.now() > parseInt(expiresIn) - TOKEN_EXPIRATION_BUFFER && hasCognitoConfig) {
    const { data, error } = await refreshToken();
    if (error || !data) {
      removeStorageItem('accessToken');
      removeStorageItem('refreshToken');
      removeStorageItem('expiresIn');
      throw { status: 401, message: 'Session expired. Please sign in again.' } satisfies ApiError;
    }
    headers['Authorization'] = `Bearer ${data.access_token}`;
  } else if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      mode: 'cors',
    });

    if (!response.ok) {
      let errorMessage = 'An error occurred';
      try {
        const errorText = await response.text();
        if (errorText) {
          try {
            const errorData = JSON.parse(errorText);
            if (errorData.detail) {
              errorMessage =
                typeof errorData.detail === 'string'
                  ? errorData.detail
                  : errorData.detail.message || errorData.detail;
            } else {
              errorMessage = errorData.message || errorMessage;
            }
          } catch {
            errorMessage = errorText || errorMessage;
          }
        }
      } catch {
        errorMessage = response.statusText || errorMessage;
      }
      throw { status: response.status, message: errorMessage } satisfies ApiError;
    }

    return { blob: await response.blob(), headers: response.headers };
  } catch (error) {
    if (isApiError(error)) throw error;
    throw {
      status: 0,
      message: error instanceof Error ? error.message : 'Network error. Please check your connection and try again.',
      isNetworkError: true,
      originalError: error,
    } satisfies ApiError;
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  const token = getStorageItem('accessToken') as string | null;
  const expiresIn = getStorageItem('expiresIn') as string | null;

  if (!token) {
    return false;
  }

  if (expiresIn && Date.now() > parseInt(expiresIn)) {
    return false;
  }

  return true;
};

/**
 * Sign out user
 */
export const signOut = (): void => {
  removeStorageItem('accessToken');
  removeStorageItem('idToken');
  removeStorageItem('refreshToken');
  removeStorageItem('expiresIn');
  removeStorageItem('landlordId');
  removeStorageItem('landlordInfo');
  // Use navigation utility for consistency
  // Note: This is in a non-React context, so window.location is appropriate
  window.location.href = '/signin';
};
